import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import './BulkQuestionGenerator.css';

const BulkQuestionGenerator = () => {
  const [combinations, setCombinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);

  const QUESTIONS_PER_GENERATION = 10;

  useEffect(() => {
    loadCombinations();
  }, []);

  const loadCombinations = async () => {
    try {
      setLoading(true);

      // Get Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/selective/admin/bulk-combinations`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) throw new Error('Failed to load combinations');

      const data = await response.json();
      setCombinations(data.combinations);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateQuestions = async (combination) => {
    const combinationId = `${combination.exam_type_id}-${combination.section_id}-${combination.domain_id}-${combination.difficulty}`;

    try {
      setGeneratingId(combinationId);

      // Update status to generating
      setCombinations(prev => prev.map(c => {
        const cId = `${c.exam_type_id}-${c.section_id}-${c.domain_id}-${c.difficulty}`;
        if (cId === combinationId) {
          return { ...c, status: 'generating', error: null };
        }
        return c;
      }));

      // Get Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/selective/admin/generate-questions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          exam_type_id: combination.exam_type_id,
          section_id: combination.section_id,
          domain_id: combination.domain_id,
          difficulty_level: combination.difficulty,
          count: QUESTIONS_PER_GENERATION
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Generation failed');
      }

      // Update status to success
      setCombinations(prev => prev.map(c => {
        const cId = `${c.exam_type_id}-${c.section_id}-${c.domain_id}-${c.difficulty}`;
        if (cId === combinationId) {
          return {
            ...c,
            status: 'success',
            generated_count: (c.generated_count || 0) + data.generated_count,
            last_generated: new Date().toISOString(),
            error: null
          };
        }
        return c;
      }));

    } catch (err) {
      // Update status to failed
      setCombinations(prev => prev.map(c => {
        const cId = `${c.exam_type_id}-${c.section_id}-${c.domain_id}-${c.difficulty}`;
        if (cId === combinationId) {
          return { ...c, status: 'failed', error: err.message };
        }
        return c;
      }));
    } finally {
      setGeneratingId(null);
    }
  };

  const getStatusBadge = (combination) => {
    const status = combination.status || 'pending';

    switch (status) {
      case 'generating':
        return <span className="status-badge status-generating">⏳ Generating...</span>;
      case 'success':
        return <span className="status-badge status-success">✓ Success</span>;
      case 'failed':
        return <span className="status-badge status-failed">✗ Failed</span>;
      default:
        return <span className="status-badge status-pending">○ Pending</span>;
    }
  };

  const generateAll = async () => {
    // Only generate for combinations that are pending or failed
    const pendingCombinations = combinations.filter(c =>
      !c.status || c.status === 'pending' || c.status === 'failed'
    );

    for (const combination of pendingCombinations) {
      if (generatingId) return; // Already generating

      await generateQuestions(combination);

      // Small delay between generations to avoid rate limits
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  };

  if (loading) {
    return (
      <div className="bulk-generator-container">
        <div className="loading">Loading combinations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bulk-generator-container">
        <div className="error-message">Error: {error}</div>
        <button onClick={loadCombinations}>Retry</button>
      </div>
    );
  }

  const stats = {
    total: combinations.length,
    pending: combinations.filter(c => !c.status || c.status === 'pending').length,
    success: combinations.filter(c => c.status === 'success').length,
    failed: combinations.filter(c => c.status === 'failed').length,
    totalGenerated: combinations.reduce((sum, c) => sum + (c.generated_count || 0), 0)
  };

  return (
    <div className="bulk-generator-container">
      <div className="bulk-header">
        <h2>Bulk Question Generator</h2>
        <button
          className="btn-generate-all"
          onClick={generateAll}
          disabled={!!generatingId}
        >
          {generatingId ? 'Generating...' : 'Generate All Pending'}
        </button>
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-label">Total Combinations:</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Pending:</span>
          <span className="stat-value pending">{stats.pending}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Success:</span>
          <span className="stat-value success">{stats.success}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Failed:</span>
          <span className="stat-value failed">{stats.failed}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Total Questions:</span>
          <span className="stat-value total">{stats.totalGenerated}</span>
        </div>
      </div>

      <div className="combinations-table">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Exam Type</th>
              <th>Section</th>
              <th>Domain</th>
              <th>Difficulty</th>
              <th>Status</th>
              <th>Generated</th>
              <th>Last Generated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {combinations.map((combo, index) => {
              const combinationId = `${combo.exam_type_id}-${combo.section_id}-${combo.domain_id}-${combo.difficulty}`;
              const isGenerating = generatingId === combinationId;
              const canGenerate = !generatingId;

              return (
                <tr key={combinationId} className={combo.status || 'pending'}>
                  <td>{index + 1}</td>
                  <td>{combo.exam_type_name}</td>
                  <td>{combo.section_name}</td>
                  <td>{combo.domain_name}</td>
                  <td>
                    <span className={`difficulty-badge ${combo.difficulty}`}>
                      {combo.difficulty}
                    </span>
                  </td>
                  <td>{getStatusBadge(combo)}</td>
                  <td>{combo.generated_count || 0}</td>
                  <td>
                    {combo.last_generated
                      ? new Date(combo.last_generated).toLocaleString()
                      : '-'
                    }
                  </td>
                  <td>
                    <div className="action-buttons">
                      {combo.status === 'failed' && (
                        <button
                          className="btn-retry"
                          onClick={() => generateQuestions(combo)}
                          disabled={!canGenerate}
                        >
                          Retry
                        </button>
                      )}
                      {combo.status === 'success' && (
                        <button
                          className="btn-generate-more"
                          onClick={() => generateQuestions(combo)}
                          disabled={!canGenerate}
                        >
                          +10 More
                        </button>
                      )}
                      {(!combo.status || combo.status === 'pending') && (
                        <button
                          className="btn-generate"
                          onClick={() => generateQuestions(combo)}
                          disabled={!canGenerate}
                        >
                          Generate
                        </button>
                      )}
                    </div>
                    {combo.error && (
                      <div className="error-text">{combo.error}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BulkQuestionGenerator;
